import { useCallback, useContext, useMemo, useState } from "react";
import findIndex from "lodash/findIndex";
import { Cell, Column } from "react-table";
import { Spinner } from "@blueprintjs/core";

import { AppToaster, DataTable, fuzzyTextFilter, Link } from "@components";
import { useBasePath } from "@hooks";
import { OrgContext, UserContext } from "@providers";
import { Feature, withToaster } from "@utils";

import { savePolicy, savePolicyRuleOverrides } from "../../api";
import {
  PolicyRuleFromPack,
  PolicyWithRules,
  RuleFromYaml,
  RuleType,
} from "../../types";

import { DeleteItemCell } from "./DeleteItemCell";
import { EditRuleCell } from "./EditRuleCell";
import { LanguagesCell } from "./LanguagesCell";
import { RuleNameCell } from "./RuleNameCell";

type Props = {
  workingPolicy: PolicyWithRules;
  setWorkingPolicy: (p: PolicyWithRules | undefined) => void;
  refreshPolicies: () => void;
  allRules: RuleFromYaml[];
  setAllRules: (r: RuleFromYaml[]) => void;
  groupBy: "rule" | "ruleset";
};

type TableProps = Omit<Props, "groupBy"> & {
  setIsSaving: (isSaving: boolean) => void;
};

const getRuleType = (rule: RuleFromYaml): RuleType => {
  const url = rule.metadata?.["semgrep.url"];
  if (
    rule.metadata?.["semgrep.ruleset"] !== undefined &&
    url &&
    url.includes("/s/")
  ) {
    return "savedRule";
  } else if (rule.metadata?.["semgrep.ruleset"] !== undefined) {
    return "rulesetRule";
  } else if (url && url.includes("/s/")) {
    return "snippet";
  } else {
    return "rule";
  }
};

const getReadableType = (rule: RuleFromYaml): string => {
  const type = getRuleType(rule);
  if (type === "rulesetRule" || type === "rule") return "registry";
  else if (type === "snippet" || type === "savedRule") return "playground";
  else return "unknown";
};

const getPolicyRuleId = (rule: RuleFromYaml): string | undefined => {
  const type = getRuleType(rule);
  if (type === "savedRule" || type === "rulesetRule") {
    return rule.metadata?.["semgrep.ruleset"];
  } else if (type === "snippet") {
    return rule.metadata!["semgrep.url"]!.slice(-4);
  } else {
    return rule.id;
  }
};

const RulesTable = ({
  workingPolicy,
  setWorkingPolicy,
  refreshPolicies,
  allRules,
  setAllRules,
  setIsSaving,
}: TableProps) => {
  const { org } = useContext(OrgContext);
  const { user } = useContext(UserContext);

  const onUpdateRuleOverride = useCallback(
    (id: string, newMessage: string) => {
      let newOverrides = workingPolicy.overrides;
      const existingOverrideIndex = newOverrides.findIndex((o) => o.id === id);
      if (existingOverrideIndex > -1) {
        newOverrides[existingOverrideIndex].message = newMessage;
      } else {
        newOverrides.push({ id: id, message: newMessage });
      }

      let newRules = allRules;
      const index = newRules.findIndex((r) => r.id === id);
      newRules[index].message = newMessage;

      setIsSaving(true);
      try {
        withToaster(
          async () => {
            await savePolicyRuleOverrides(org!.id, workingPolicy);
          },
          (err) => `Error saving rule message override: ${err.body?.error}`
        )()
          .then(() => {
            AppToaster.show({
              message: `Rule message successfully saved.`,
              intent: "success",
            });
            setWorkingPolicy({ ...workingPolicy, overrides: newOverrides });
            setAllRules(newRules);
          })
          .finally(refreshPolicies);
      } finally {
        setIsSaving(false);
      }
    },
    [
      org,
      workingPolicy,
      setWorkingPolicy,
      refreshPolicies,
      allRules,
      setAllRules,
      setIsSaving,
    ]
  );

  const onDeleteRule = useCallback(
    (rule: RuleFromYaml) => {
      setIsSaving(true);
      const ruleType = getRuleType(rule);
      const policyRuleId = getPolicyRuleId(rule);
      let newPolicyRules = [...workingPolicy!.rules];
      if (ruleType === "rulesetRule") {
        // This is technically adding an override
        // Find PolicyRule in policy
        const newPolicyRuleArray = [...workingPolicy!.rules];
        const ruleIndex = findIndex(
          newPolicyRules,
          (r) => r.id === policyRuleId
        );
        if (ruleIndex < 0) return;

        // Change overrides
        const newPolicyRule = newPolicyRuleArray[
          ruleIndex
        ] as PolicyRuleFromPack;
        newPolicyRule.overrides.push({ id: rule.id });
        newPolicyRules[ruleIndex] = newPolicyRule;
      } else {
        newPolicyRules = newPolicyRules.filter(
          (r) => r.id.toLowerCase() !== policyRuleId?.toLowerCase()
        );
      }

      const newPolicy = { ...workingPolicy!, rules: newPolicyRules };
      try {
        withToaster(
          async () => {
            await savePolicy(org!.id, newPolicy);
          },
          (err) => `Error saving policy: ${err.body?.error}`
        )()
          .then(() => {
            AppToaster.show({
              message: `Rule successfully removed from policy.`,
              intent: "success",
            });
            setWorkingPolicy(newPolicy);
            setAllRules(allRules.filter((r) => r !== rule));
          })
          .finally(refreshPolicies);
      } finally {
        setIsSaving(false);
      }
    },
    [
      org,
      workingPolicy,
      setWorkingPolicy,
      refreshPolicies,
      allRules,
      setAllRules,
      setIsSaving,
    ]
  );

  const columns: Column<RuleFromYaml>[] = useMemo(
    () => [
      {
        Header: "Name",
        accessor: (rule: RuleFromYaml) => getPolicyRuleId(rule),
        Cell: ({ row }: Cell<RuleFromYaml>) => (
          <RuleNameCell
            rule={row.original}
            ruleType={getRuleType(row.original)}
          />
        ),
      },
      {
        Header: "Languages",
        accessor: (rule: RuleFromYaml) => rule.languages.join(","),
        Cell: ({ row }: Cell<RuleFromYaml>) => (
          <LanguagesCell languages={row.original.languages} />
        ),
      },
      {
        Header: "Source",
        accessor: (rule: RuleFromYaml) => getReadableType(rule),
        Cell: ({ row }: Cell<RuleFromYaml>) => (
          <div> {getReadableType(row.original)}</div>
        ),
      },
      {
        id: "edit",
        accessor: "id",
        Header: "",
        disableSortBy: true,
        Cell: ({ row }: Cell<RuleFromYaml>) => (
          <>
            {Feature.hasTeamsTier({ user, org }) && (
              <EditRuleCell
                rule={row.original}
                onUpdateRuleOverride={onUpdateRuleOverride}
              />
            )}
            <DeleteItemCell
              itemId={row.original.id}
              onDelete={() => onDeleteRule(row.original)}
            />
          </>
        ),
      },
    ],
    [user, org, onUpdateRuleOverride, onDeleteRule]
  );

  return (
    <DataTable
      columns={columns}
      data={allRules}
      pagination={{ pageSize: 20, disabled: false }}
      filter={fuzzyTextFilter}
      sorting
    />
  );
};

const RulesetTable = ({
  workingPolicy,
  setWorkingPolicy,
  refreshPolicies,
  allRules,
  setAllRules,
  setIsSaving,
}: TableProps) => {
  const { org } = useContext(OrgContext);

  const onDeleteRuleset = useCallback(
    (ruleset: PolicyRuleFromPack) => {
      setIsSaving(true);
      const newPolicy = {
        ...workingPolicy!,
        rules: [
          ...workingPolicy!.rules.filter(
            // Keep all of the individual rules, filter out the specific pack that has been deleted
            ({ id, type }) =>
              type !== "pack" || (type === "pack" && id !== ruleset.id)
          ),
        ],
      };
      try {
        withToaster(
          async () => {
            await savePolicy(org!.id, newPolicy);
          },
          (err) => `Error saving policy: ${err.body?.error}`
        )()
          .then(() => {
            AppToaster.show({
              message: `Ruleset successfully removed from policy.`,
              intent: "success",
            });
            setWorkingPolicy(newPolicy);
            setAllRules(
              allRules.filter(
                ({ metadata }) => metadata?.["semgrep.ruleset"] !== ruleset.id
              )
            );
          })
          .finally(refreshPolicies);
      } finally {
        setIsSaving(false);
      }
    },
    [
      org,
      workingPolicy,
      setWorkingPolicy,
      refreshPolicies,
      allRules,
      setAllRules,
      setIsSaving,
    ]
  );

  const columns: Column<PolicyRuleFromPack>[] = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "id",
      },
      {
        id: "edit",
        accessor: "id",
        Header: "",
        disableSortBy: true,
        Cell: ({ row }: Cell<PolicyRuleFromPack>) => (
          <DeleteItemCell
            itemId={row.original.id}
            onDelete={() => onDeleteRuleset(row.original)}
          />
        ),
      },
    ],
    [onDeleteRuleset]
  );

  const data: PolicyRuleFromPack[] = useMemo(
    () =>
      workingPolicy!.rules
        .filter(({ type }) => type === "pack")
        .map((item) => item as PolicyRuleFromPack),
    [workingPolicy]
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      pagination={{ pageSize: 20, disabled: false }}
      filter={fuzzyTextFilter}
      sorting
    />
  );
};

export const PolicyInfo = ({
  workingPolicy,
  setWorkingPolicy,
  refreshPolicies,
  allRules,
  setAllRules,
  groupBy = "rule",
}: Props) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const basePath = useBasePath();

  const policyHasRules =
    workingPolicy !== undefined && workingPolicy.rules.length > 0;

  return (
    <>
      {isSaving ? (
        <Spinner intent="primary" />
      ) : workingPolicy === undefined ? (
        <h3>Policy not found</h3>
      ) : (
        <>
          {policyHasRules ? (
            <div style={{ marginTop: "var(--global-spacing-l)" }}>
              {groupBy === "rule" ? (
                <RulesTable
                  workingPolicy={workingPolicy}
                  setWorkingPolicy={setWorkingPolicy}
                  refreshPolicies={refreshPolicies}
                  allRules={allRules}
                  setAllRules={setAllRules}
                  setIsSaving={setIsSaving}
                />
              ) : (
                <RulesetTable
                  workingPolicy={workingPolicy}
                  setWorkingPolicy={setWorkingPolicy}
                  refreshPolicies={refreshPolicies}
                  allRules={allRules}
                  setAllRules={setAllRules}
                  setIsSaving={setIsSaving}
                />
              )}
            </div>
          ) : (
            <h3>
              This policy is empty! To add rules, explore the{" "}
              <Link to="/r">Semgrep Registry</Link> or{" "}
              <Link to={`${basePath}/editor`}>write your own</Link>.
            </h3>
          )}
        </>
      )}
    </>
  );
};
